import { type RefObject, useLayoutEffect, useState } from 'react';

/**
 * Tracks a virtual canvas's top edge in the coordinate space of a shared
 * scrolling element. TanStack Virtual uses this margin to calculate ranges for
 * nested canvases without giving each canvas its own scroll container.
 */
export function useVirtualScrollMargin(
  scrollElementRef: RefObject<HTMLElement | null>,
  canvasRef: RefObject<HTMLElement | null>,
  positionKey: unknown,
) {
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    // Reading the key makes position-only layout changes rerun this measurement.
    void positionKey;
    const scrollElement = scrollElementRef.current;
    const canvas = canvasRef.current;
    if (!scrollElement || !canvas) return;

    const measure = () => {
      const nextMargin =
        canvas.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop;
      setScrollMargin((currentMargin) =>
        currentMargin === nextMargin ? currentMargin : nextMargin,
      );
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(scrollElement);
    resizeObserver.observe(canvas);

    return () => resizeObserver.disconnect();
  }, [canvasRef, positionKey, scrollElementRef]);

  return scrollMargin;
}
